"use client"

import { useState } from "react"
import { CheckCircle2, Flag, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { generateVoteFingerprint } from "@/lib/community/vote-fingerprint"
import { cn } from "@/lib/utils"
import { communityAnalytics } from "@/lib/analytics"

const REPORT_REASONS = [
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'spam', label: 'Spam or promotion' },
  { value: 'wrong_category', label: 'Not a lawn photo' },
  { value: 'duplicate', label: 'Duplicate submission' },
  { value: 'copyright', label: 'Copyright or permission concern' },
  { value: 'other', label: 'Something else' },
] as const

interface ReportPhotoDialogProps {
  photoId: string
  photoTitle: string
  compact?: boolean
  className?: string
}

export function ReportPhotoDialog({
  photoId,
  photoTitle,
  compact = false,
  className,
}: ReportPhotoDialogProps) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<(typeof REPORT_REASONS)[number]['value']>('inappropriate_content')
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && submitted) {
      setSubmitted(false)
      setReason('inappropriate_content')
      setDetails('')
    }
    setError(null)
    setOpen(nextOpen)
  }

  const submitReport = async () => {
    setSubmitting(true)
    setError(null)

    try {
      const fingerprint = await generateVoteFingerprint()
      const response = await fetch(`/api/community/photos/${photoId}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Voter-Fingerprint': fingerprint,
        },
        body: JSON.stringify({ reason, details: details.trim() || null }),
      })
      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(payload.error || 'Failed to submit report')
      }

      setSubmitted(true)
      communityAnalytics.reportSubmitted(photoId, reason)
      toast.success('Report sent to the moderation team.')
    } catch (reportError) {
      const message = reportError instanceof Error ? reportError.message : 'Failed to submit report'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-green-950/65 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2",
            compact && "min-w-11 px-2",
            className,
          )}
          aria-label={compact ? `Report ${photoTitle}` : undefined}
        >
          <Flag className="h-4 w-4" aria-hidden="true" />
          {!compact && 'Report'}
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-lg rounded-3xl p-0">
        {submitted ? (
          <div className="p-7 text-center sm:p-8">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" aria-hidden="true" />
            <DialogHeader className="mt-4">
              <DialogTitle>Thanks for the heads-up</DialogTitle>
              <DialogDescription>
                A moderator will review {photoTitle}. The photo stays visible unless it breaks the gallery rules.
              </DialogDescription>
            </DialogHeader>
            <DialogClose asChild>
              <Button className="mt-6 min-h-11 w-full">Done</Button>
            </DialogClose>
          </div>
        ) : (
          <div className="p-6 sm:p-7">
            <DialogHeader>
              <DialogTitle>Report this photo</DialogTitle>
              <DialogDescription>
                Tell us what is wrong with {photoTitle}. Reports go only to the moderation team.
              </DialogDescription>
            </DialogHeader>

            <RadioGroup
              value={reason}
              onValueChange={(value) => setReason(value as (typeof REPORT_REASONS)[number]['value'])}
              className="mt-6 gap-2"
              aria-label="Report reason"
            >
              {REPORT_REASONS.map((option) => (
                <Label
                  key={option.value}
                  htmlFor={`report-${photoId}-${option.value}`}
                  className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-green-950/10 px-3 py-2.5 font-normal text-green-950 hover:bg-green-50"
                >
                  <RadioGroupItem id={`report-${photoId}-${option.value}`} value={option.value} />
                  {option.label}
                </Label>
              ))}
            </RadioGroup>

            <div className="mt-5">
              <Label htmlFor={`report-details-${photoId}`}>Details (optional)</Label>
              <Textarea
                id={`report-details-${photoId}`}
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Add anything that will help a moderator review it."
                className="mt-2"
              />
              <p className="mt-1 text-right text-xs text-green-950/50">{details.length}/500</p>
            </div>

            {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}

            <DialogFooter className="mt-6 gap-2 sm:gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={submitting}>Cancel</Button>
              </DialogClose>
              <Button type="button" variant="destructive" onClick={submitReport} disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                Send report
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
